'use client';

import * as amplitude from '@amplitude/analytics-browser';
import { Experiment, type ExperimentClient } from '@amplitude/experiment-js-client';
import { ampli, flushQueuedAmpliCalls } from '@magicschool/ampli-client';
import { logger } from '@magicschool/logger';
import type { ThemeName, UseThemeProps } from '@magicschool/spellbook';
import type { MagicSchoolUser } from '@magicschool/supabase/user';
import { isProduction } from '@magicschool/utils/nextjs/metadata';
import { sendGAEvent } from '@next/third-parties/google';
import type { TeacherProfileWithRelations } from '@/app/(teacher)/profile/_actions/types';
import { getCurrentCookieYesConsent } from '@/components/CookieYes/getCurrentCookieYesConsent';
import { deleteCookie } from '@/util/cookies';
import { redactAuthEventProperties } from '@/util/redactAuthTokens';
import {
  AMPLITUDE_ANALYTICS_PROXY_URL,
  AMPLITUDE_API_KEY,
  AMPLITUDE_EXPERIMENT_DEPLOYMENT_KEY,
  AMPLITUDE_EXPERIMENT_PROXY_URL,
  USE_AMPLITUDE,
} from './constants';
import type { AnalyticsUserProperties, SharedAnalyticsUserPropertyArgs } from './types';
import { buildAmplitudeUserProperties, buildIdentifyObj, stripNullAndUndefinedProperties } from './util';

let userPropertiesCache: Partial<AnalyticsUserProperties> = {};
// May be undefined if initAmplitudeExperiment hasn't created it yet. Read this directly only for
// optional/read-if-ready checks (feature flag hooks, logout cleanup) where it'd be wrong to force
// creation. Anything that needs a guaranteed client should call getExperimentClient() instead.
export let experiment: ExperimentClient;
export let amplitudeExperimentsFetched = false;

// Reads live CookieYes state on every call
const isAnalyticsConsentGranted = () => getCurrentCookieYesConsent().analytics;

// Subscription system for amplitude experiments readiness
type ExperimentsReadySubscriber = (ready: boolean) => void;
const experimentsReadySubscribers = new Set<ExperimentsReadySubscriber>();

// Tracks the last value broadcast to subscribers so duplicate calls don't fire `true→true`.
// Reset to null on logout so post-logout broadcasts aren't suppressed.
let lastBroadcastValue: boolean | null = null;

export const subscribeToExperimentsReady = (callback: ExperimentsReadySubscriber) => {
  experimentsReadySubscribers.add(callback);
  callback(amplitudeExperimentsFetched);
  return () => {
    experimentsReadySubscribers.delete(callback);
  };
};

const broadcastExperimentsReady = () => {
  if (amplitudeExperimentsFetched === lastBroadcastValue) return;
  lastBroadcastValue = amplitudeExperimentsFetched;
  experimentsReadySubscribers.forEach((cb) => cb(amplitudeExperimentsFetched));
};

const AMPLITUDE_CONFIG: amplitude.Types.BrowserOptions = {
  autocapture: {
    attribution: {
      excludeReferrers: ['^(?:(?:.*.)?magicschool.ai|accounts.google.com|login.microsoftonline.com|clever.com|classlink.com)$'],
      resetSessionOnNewCampaign: false,
    },
    sessions: true,
    pageViews: { trackHistoryChanges: 'pathOnly' },
    formInteractions: false,
    fileDownloads: false,
    webVitals: true,
  },
  serverUrl: AMPLITUDE_ANALYTICS_PROXY_URL,
  remoteConfig: {
    serverUrl: AMPLITUDE_ANALYTICS_PROXY_URL,
  },
};

if (isProduction()) {
  AMPLITUDE_CONFIG.cookieOptions = {
    domain: '.magicschool.ai',
  };
}

// True once the client's own fetch has run — distinct from amplitudeExperimentsFetched, which
// can also become true from a server-seeded mount with no client fetch at all.
let clientVariantsFetchAttempted = false;

// Lazily creates the experiment client if initAmplitudeExperiment hasn't run yet, then returns it.
// Prefer this over reading `experiment` directly wherever a guaranteed client is required —
// Experiment.initializeWithAmplitudeAnalytics is backed by the SDK's own singleton cache (keyed by
// deployment key/instance name), so calling it more than once is safe and just returns the same
// client. That means whichever caller gets here first (initAmplitudeExperiment's own init, or a
// racing updateAnalyticsUserProperties call from e.g. DarkModeToggle/authStore) can create it,
// instead of fetchAmplitudeExperimentVariants having to wait for one specific caller to run first.
const getExperimentClient = (initialVariants?: ExperimentClient['variants']): ExperimentClient => {
  experiment ??= Experiment.initializeWithAmplitudeAnalytics(AMPLITUDE_EXPERIMENT_DEPLOYMENT_KEY, {
    initialVariants,
    serverUrl: AMPLITUDE_EXPERIMENT_PROXY_URL,
  });
  return experiment;
};

const fetchAmplitudeExperimentVariants = async (userPropertiesToSet: Partial<AnalyticsUserProperties>) => {
  if (!USE_AMPLITUDE || !AMPLITUDE_API_KEY) return;

  if (!AMPLITUDE_EXPERIMENT_DEPLOYMENT_KEY) {
    logger.error('Missing Amplitude Experiment Client Deployment Key');
    return;
  } else if (!userPropertiesCache.id) {
    logger.error('Cannot fetch Amplitude Experiment variants: userPropertiesCache.id is undefined');
    return;
  }
  const validUserProperties = stripNullAndUndefinedProperties(userPropertiesToSet);
  try {
    await getExperimentClient().fetch({ user_id: userPropertiesCache.id, user_properties: validUserProperties });
  } catch (error) {
    logger.warn('Amplitude experiment client fetch failed, using fallback values', error);
  } finally {
    amplitudeExperimentsFetched = true;
    clientVariantsFetchAttempted = true;
    broadcastExperimentsReady();
  }
};

// Guards initAmplitudeExperiment's recovery path and setInitialAnalyticsUserProperties from
// issuing concurrent, duplicate experiment.fetch() calls when both run on the same mount
// (consent granted + no server-seeded variants triggers both).
let variantsFetchPromise: Promise<void> | null = null;

const fetchExperimentVariantsOnceForUser = ({ user, teacherProfile, appTheme, userSystemTheme }: SharedAnalyticsUserPropertyArgs) => {
  userPropertiesCache = buildAmplitudeUserProperties({
    user,
    teacherProfile,
    appTheme,
    userSystemTheme,
    cachedProperties: userPropertiesCache,
  }) as Partial<AnalyticsUserProperties>;

  if (clientVariantsFetchAttempted) return Promise.resolve();
  variantsFetchPromise ??= fetchAmplitudeExperimentVariants(userPropertiesCache).finally(() => {
    variantsFetchPromise = null;
  });
  return variantsFetchPromise;
};

type SetInitialAnalyticsUserProperties = SharedAnalyticsUserPropertyArgs & {
  user: MagicSchoolUser;
};

const setInitialAnalyticsUserProperties = async ({
  user,
  teacherProfile,
  appTheme,
  userSystemTheme,
}: SetInitialAnalyticsUserProperties): Promise<void> => {
  await fetchExperimentVariantsOnceForUser({ user, teacherProfile, appTheme, userSystemTheme });

  // amplitude.identify() is safe to call before consent resolves — the SDK queues it until
  // amplitude.init() runs, which only happens once consent is actually granted. GTM has no such
  // queue, so it stays gated.
  const identify = new amplitude.Identify();
  buildIdentifyObj(identify, userPropertiesCache);
  amplitude.identify(identify);

  if (isAnalyticsConsentGranted()) {
    // GTM depends on dataLayer updates
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'setUserTraits',
      ...userPropertiesCache,
    });
  }
};

export const updateAnalyticsUserProperties = async (propertiesToUpdate: Partial<AnalyticsUserProperties>) => {
  userPropertiesCache = {
    ...userPropertiesCache,
    ...propertiesToUpdate,
  };

  const identify = new amplitude.Identify();
  buildIdentifyObj(identify, propertiesToUpdate);
  amplitude.identify(identify);

  await fetchAmplitudeExperimentVariants(propertiesToUpdate);

  if (isAnalyticsConsentGranted()) {
    // GTM depends on dataLayer updates
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'setUserTraits',
      ...userPropertiesCache,
    });
  }
};

export type InitAmplitudeAnalyticsOptions = {
  user?: MagicSchoolUser;
  teacherProfile?: TeacherProfileWithRelations;
  appTheme?: ThemeName;
  userSystemTheme?: UseThemeProps['systemTheme'];
};

export type InitAmplitudeExperimentOptions = InitAmplitudeAnalyticsOptions & {
  initialVariants?: ExperimentClient['variants'];
};

// Feature flags must keep working regardless of analytics consent, so this has no consent
// check — see CMP-77: the Experiment SDK never writes a cookie, only amplitude.init() does.
export const initAmplitudeExperiment = async ({
  user,
  teacherProfile,
  appTheme,
  userSystemTheme,
  initialVariants,
}: InitAmplitudeExperimentOptions = {}) => {
  // by default we don't run on our local environments
  if (!USE_AMPLITUDE) {
    // Amplitude is disabled — signal ready immediately so consumers receive fallback values.
    amplitudeExperimentsFetched = true;
    broadcastExperimentsReady();
    return;
  }

  if (!AMPLITUDE_EXPERIMENT_DEPLOYMENT_KEY) {
    logger.error('Missing Amplitude Experiment Client Deployment Key');
    // Signal ready so the UI isn't permanently blocked; all flags return fallback values.
    amplitudeExperimentsFetched = true;
    broadcastExperimentsReady();
    return;
  }

  // Set up amplitude experiment SDK
  getExperimentClient(initialVariants);

  if (initialVariants !== undefined) {
    // Server-seeded values are available synchronously — mark ready now, don't wait on the
    // client-only enrichment fetch below.
    amplitudeExperimentsFetched = true;
    broadcastExperimentsReady();
  }

  // Always enrich with the client-only property set, regardless of consent or server-seeding —
  // flags aren't opt-out-able, only analytics tracking is.
  if (!user) return;
  await fetchExperimentVariantsOnceForUser({ user, teacherProfile, appTheme, userSystemTheme });
};

export const initAmplitudeAnalytics = async ({ user, teacherProfile, appTheme, userSystemTheme }: InitAmplitudeAnalyticsOptions = {}) => {
  // by default we don't run on our local environments
  if (!USE_AMPLITUDE || !AMPLITUDE_API_KEY) return;

  amplitude.add({
    name: 'redact-auth-tokens',
    type: 'enrichment',
    execute: (event) => {
      if (event.event_properties) {
        event.event_properties = redactAuthEventProperties(event.event_properties);
      }
      return Promise.resolve(event);
    },
  });
  const amplitudeInit = amplitude.init(AMPLITUDE_API_KEY, user?.id, AMPLITUDE_CONFIG);

  // Opt Out of Amplitude for various reasons including, but not limited to, Demo Users
  amplitude.setOptOut(user?.is_demo === true);

  ampli.load({ client: { instance: amplitude } });
  flushQueuedAmpliCalls();

  if (user) {
    await setInitialAnalyticsUserProperties({ user, teacherProfile, appTheme, userSystemTheme });
  } else {
    // This will prevent a previous user's anonymous logout event from being
    // attributed to the next user's login flow
    amplitude.reset();
  }

  window.amplitude = amplitude;

  // Set up amplitude guides & surveys. Imported here, after window.amplitude is set, because the
  // plugin searches for amplitude as soon as it is imported.
  // Awaited, not left floating: an un-awaited import() keeps running after this function returns
  // and then touches window with nobody left to own the outcome — which under vitest lands after
  // the test environment is gone and fails the run with "window is not defined".
  // Registered before Session Replay below: Session Replay's own await (on plugin setup / remote
  // config) must not delay window.engagementReady, which e2e gates every sidebar navigation on.
  try {
    const { plugin: engagementPlugin } = await import('@amplitude/engagement-browser');
    amplitude.add(engagementPlugin());

    logger.info('Amplitude Engagement Plugin ready');
    window.engagementReady = true;
  } catch (error) {
    // Guides & surveys are additive — failing to load them must not fail analytics init.
    logger.warn('Amplitude Engagement Plugin failed to load', error);
  }

  // Session Replay is additive — failing to import or finish plugin setup must not fail analytics init.
  // Not loaded for students, matching Clarity and Datadog RUM, which also hard-gate session
  // recording on user_role rather than relying on consent alone.
  // sampleRate is 0 in code: Amplitude Session Replay remote config is the production rollout
  // control, so capture is not enabled broadly from this bootstrap.
  // Replay config/upload use Amplitude's SR hosts (sr-client-cfg.amplitude.com / api-sr.amplitude.com),
  // not AMPLITUDE_ANALYTICS_PROXY_URL (/atd/). That proxy terminates Browser SDK ingest and remote
  // config, not Session Replay's /config and /sessions/v2/track paths. Passing it as trackServerUrl /
  // configServerUrl would send replay to the wrong endpoints until dedicated proxy routes exist.
  if (user?.user_role !== 'student') {
    try {
      // amplitude.add() only returns the plugin's real setup() promise once the client is
      // `isReady` — otherwise it queues the registration and resolves immediately (see
      // AmplitudeCore.add in @amplitude/analytics-core), so this catch would never see a setup()
      // rejection. Awaiting the init() call's own promise first makes that guarantee real.
      await amplitudeInit.promise;

      const { sessionReplayPlugin } = await import('@amplitude/plugin-session-replay-browser');
      await amplitude.add(
        sessionReplayPlugin({
          sampleRate: 0,
          privacyConfig: {
            defaultMaskLevel: 'conservative',
          },
        }),
      ).promise;

      logger.info('Amplitude Session Replay Plugin ready');
    } catch (error) {
      logger.warn('Amplitude Session Replay Plugin failed to load', error);
    }
  }
};

// Cookie name follows @amplitude/analytics-core's getCookieName: AMP_<first 10 chars of api key>
const AMPLITUDE_COOKIE_NAME = `AMP_${AMPLITUDE_API_KEY.substring(0, 10)}`;

export const revokeAnalyticsConsent = () => {
  if (!USE_AMPLITUDE || !AMPLITUDE_API_KEY) return;

  amplitude.setOptOut(true);
  amplitude.reset();
  // Must match the domain the SDK wrote the cookie with (AMPLITUDE_CONFIG.cookieOptions above),
  // or this expires a different, host-only cookie and leaves the real one in place.
  deleteCookie(AMPLITUDE_COOKIE_NAME, '/', { domain: AMPLITUDE_CONFIG.cookieOptions?.domain });
};

export const setAmplitudeUserId = (userId: string | null) => {
  if (userId) {
    amplitude.setUserId(userId);
  }
};

export const analyticsTrack = (eventType: string, eventProperties?: Record<string, unknown>) => {
  const properties = redactAuthEventProperties(eventProperties ?? {});

  // Safe to call before consent resolves — amplitude.track() queues until amplitude.init() runs,
  // which only happens once consent is actually granted.
  amplitude.track(eventType, properties);

  if (!isAnalyticsConsentGranted()) return;

  sendGAEvent(eventType, properties);

  // GTM depends on dataLayer updates
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: eventType, ...properties });
};

export const analyticsHandleUserLogout = () => {
  userPropertiesCache = {};
  amplitudeExperimentsFetched = false;
  clientVariantsFetchAttempted = false;
  lastBroadcastValue = null;
  experiment?.clear();

  amplitude.track('User Logout', { isAnonymous: true });

  // Dispatch queued events before logout
  amplitude.flush();
  amplitude.reset();

  if (!isAnalyticsConsentGranted()) return;

  // GTM depends on dataLayer updates
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: 'User Logout', isAnonymous: true });
};
